import { useEffect, useRef, useState } from "react";

import { TryOnRenderer } from "./lib/try-on-renderer";

/**
 * Owns a TryOnRenderer for as long as it is mounted, and keeps it fed.
 *
 * Everything that runs per-frame lives inside the renderer, outside React: the
 * only job here is lifecycle and the handful of inputs that genuinely change.
 *
 * @param {object} props
 * @param {object} props.frame  Catalog entry to wear — see lib/catalog.js.
 * @param {React.RefObject} props.landmarksRef  Latest landmarks, written by the tracker.
 * @param {React.RefObject} props.onDetectRef  Filled with "draw now"; the tracker
 *   calls it once per detection, which becomes the entire render loop.
 * @param {React.RefObject} props.videoRef  The camera feed, painted by the scene
 *   itself rather than composited behind it.
 * @param {{width: number, height: number}} props.sourceSize
 * @param {{width: number, height: number}} props.containerSize
 */
export function TryOnScene({
  frame,
  landmarksRef,
  onDetectRef,
  videoRef,
  sourceSize,
  containerSize,
  mirrored = true,
  fit = "cover",
}) {
  const hostRef = useRef(null);
  const rendererRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);

  // Declared first so it has run — and rendererRef is populated — by the time
  // any effect below looks for it. Never re-runs; the renderer outlives every
  // input change.
  useEffect(() => {
    const renderer = new TryOnRenderer(hostRef.current);
    rendererRef.current = renderer;
    return () => {
      rendererRef.current = null;
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    rendererRef.current?.setVideo(videoRef.current);
  }, [videoRef]);

  useEffect(() => {
    rendererRef.current?.setLayout({ sourceSize, containerSize, mirrored, fit });
  }, [sourceSize, containerSize, mirrored, fit]);

  useEffect(() => {
    let cancelled = false;
    setFrameReady(false);
    rendererRef.current?.setFrame(frame).then(() => {
      if (!cancelled) setFrameReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [frame]);

  useEffect(() => {
    onDetectRef.current = () => rendererRef.current?.frame(landmarksRef.current);
    return () => {
      onDetectRef.current = null;
    };
  }, [onDetectRef, landmarksRef]);

  return (
    <>
      <div ref={hostRef} className="vto-scene" />
      {!frameReady && <p className="vto-pill vto-pill--top">Loading frame…</p>}
    </>
  );
}
